"use client";

import React, { useEffect, useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumb from '@/components/Breadcrumb';
import Icon from '@/components/ui/AppIcon';

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); } }),
      { threshold: 0.1 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

interface FormData {
  name: string;
  email: string;
  company: string;
  service: string;
  budget: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

const services = [
  'Brand Strategy',
  'Digital Transformation',
  'Creative Production',
  'Growth Consulting',
  'UX & Product Design',
  'Content Marketing',
  'Multiple / Not sure',
];

const budgets = [
  'Under $10,000',
  '$10,000 – $25,000',
  '$25,000 – $50,000',
  '$50,000 – $100,000',
  '$100,000+',
];

const offices = [
  {
    city: 'New York',
    address: '245 Park Avenue, Suite 1600',
    cityState: 'New York, NY 10167',
    country: 'United States',
    phone: '+1 (212) 555-0182',
    email: 'newyork@businesssite.com',
    lat: '40.7549',
    lon: '-73.9718',
  },
  {
    city: 'Los Angeles',
    address: '2000 Avenue of the Stars, Suite 400',
    cityState: 'Los Angeles, CA 90067',
    country: 'United States',
    phone: '+1 (310) 555-0247',
    email: 'la@businesssite.com',
    lat: '34.0584',
    lon: '-118.4166',
  },
];

export default function ContactPage() {
  useReveal();

  const [form, setForm] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    service: '',
    budget: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validate = (): boolean => {
    const errs: FormErrors = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) {
      errs.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = 'Please enter a valid email address';
    }
    if (!form.message.trim()) errs.message = 'Please tell us about your project';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    /* Backend connection point — replace with actual API call */
    await new Promise((r) => setTimeout(r, 1400));
    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "BusinessSite",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "Customer Service",
              "email": "hello@businesssite.com",
            },
            "location": offices.map((o) => ({
              "@type": "Place",
              "name": `BusinessSite ${o.city}`,
              "address": {
                "@type": "PostalAddress",
                "streetAddress": o.address,
                "addressLocality": o.cityState.split(',')[0],
                "addressRegion": o.cityState.split(', ')[1]?.split(' ')[0],
                "postalCode": o.cityState.split(' ').pop(),
                "addressCountry": "US",
              },
              "telephone": o.phone,
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": o.lat,
                "longitude": o.lon,
              },
            })),
          })
        }}
      />

      <Header />

      <main className="bg-background overflow-x-hidden">

        {/* ── LIGHTWEIGHT HERO ── */}
        <section
          className="relative pt-32 pb-20 px-6 md:px-12 overflow-hidden"
          style={{ background: 'var(--color-primary)' }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse 60% 70% at 30% 60%, rgba(201,80,42,0.15) 0%, transparent 70%)',
            }}
          />
          <div className="relative z-10" style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
            <div className="mb-8">
              <Breadcrumb crumbs={[{ label: 'Contact' }]} />
            </div>
            <div className="max-w-2xl reveal">
              <span className="section-label mb-6 block" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Get in Touch
              </span>
              <h1
                className="fluid-h1 font-display font-light leading-none text-white mb-6"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Let's start<br />
                <em className="not-italic text-accent">a conversation.</em>
              </h1>
              <p className="text-white/60 text-lg leading-relaxed max-w-lg">
                Tell us about your project and we'll respond within 24 hours with a tailored perspective — no sales pitch, just honest thinking.
              </p>
            </div>
          </div>
        </section>

        {/* ── MAIN CONTACT AREA ── */}
        <section className="py-20 px-6 md:px-12" style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">

            {/* ── FORM ── */}
            <div className="lg:col-span-3 reveal">
              {submitted ? (
                <div className="flex flex-col items-start gap-6 py-16">
                  <div className="w-16 h-16 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center">
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                      <path d="M5 14l7 7L23 7" stroke="var(--color-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h2
                      className="text-3xl font-display font-light text-primary mb-3"
                      style={{ fontFamily: 'var(--font-display)' }}
                    >
                      Message received.
                    </h2>
                    <p className="text-muted leading-relaxed max-w-md">
                      Thanks for reaching out, {form.name.split(' ')[0]}. We'll review your brief and get back to you within 24 hours.
                    </p>
                  </div>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name:'',email:'',company:'',service:'',budget:'',message:'' }); }}
                    className="btn-outline mt-4"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate aria-label="Project inquiry form">
                  <h2
                    className="text-2xl font-display font-light text-primary mb-10"
                    style={{ fontFamily: 'var(--font-display)' }}
                  >
                    Tell us about your project
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                    {/* Name */}
                    <div>
                      <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-2" htmlFor="name">
                        Full Name <span className="text-accent">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Alexandra Voss"
                        className={`form-input ${errors.name ? 'error' : ''}`}
                        aria-required="true"
                        aria-describedby={errors.name ? 'name-error' : undefined}
                      />
                      {errors.name && (
                        <p id="name-error" className="text-xs text-red-500 mt-1.5 font-medium" role="alert">{errors.name}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-2" htmlFor="email">
                        Work Email <span className="text-accent">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="alex@company.com"
                        className={`form-input ${errors.email ? 'error' : ''}`}
                        aria-required="true"
                        aria-describedby={errors.email ? 'email-error' : undefined}
                      />
                      {errors.email && (
                        <p id="email-error" className="text-xs text-red-500 mt-1.5 font-medium" role="alert">{errors.email}</p>
                      )}
                    </div>

                    {/* Company */}
                    <div>
                      <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-2" htmlFor="company">
                        Company
                      </label>
                      <input
                        id="company"
                        name="company"
                        type="text"
                        autoComplete="organization"
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Meridian Brands"
                        className="form-input"
                      />
                    </div>

                    {/* Service */}
                    <div>
                      <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-2" htmlFor="service">
                        Service Area
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={form.service}
                        onChange={handleChange}
                        className="form-input"
                        style={{ cursor: 'pointer' }}
                      >
                        <option value="">Select a service...</option>
                        {services.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    {/* Budget */}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-3" htmlFor="budget">
                        Approximate Budget
                      </label>
                      <div className="flex flex-wrap gap-3">
                        {budgets.map((b) => (
                          <button
                            key={b}
                            type="button"
                            onClick={() => setForm((p) => ({ ...p, budget: b }))}
                            className={`text-sm font-medium px-4 py-2 rounded-full border transition-all duration-200 ${
                              form.budget === b
                                ? 'bg-primary text-white border-primary' :'bg-transparent text-muted border-border hover:border-primary hover:text-primary'
                            }`}
                          >
                            {b}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Message */}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-2" htmlFor="message">
                        Project Brief <span className="text-accent">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={form.message}
                        onChange={handleChange}
                        placeholder="Tell us about your goals, timeline, and what success looks like for you..."
                        className={`form-input resize-none ${errors.message ? 'error' : ''}`}
                        aria-required="true"
                        aria-describedby={errors.message ? 'message-error' : undefined}
                      />
                      {errors.message && (
                        <p id="message-error" className="text-xs text-red-500 mt-1.5 font-medium" role="alert">{errors.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-10 flex items-center gap-6">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary"
                      aria-busy={submitting}
                    >
                      {submitting ? (
                        <>
                          <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white spinner" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Brief
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </>
                      )}
                    </button>
                    <p className="text-xs text-muted">
                      We respond within 24 hours. No spam, ever.
                    </p>
                  </div>
                </form>
              )}
            </div>

            {/* ── SIDEBAR ── */}
            <div className="lg:col-span-2 space-y-8 reveal reveal-delay-2">

              {/* Trust signals */}
              <div className="bg-card border border-border rounded-2xl p-8">
                <h3
                  className="text-lg font-display font-light text-primary mb-6"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  What to expect
                </h3>
                <ul className="space-y-4">
                  {[
                    { icon: 'ClockIcon', text: 'Response within 24 hours' },
                    { icon: 'UserGroupIcon', text: 'Direct access to senior team' },
                    { icon: 'DocumentTextIcon', text: 'Tailored proposal — no templates' },
                    { icon: 'ShieldCheckIcon', text: 'NDA available on request' },
                  ].map((item) => (
                    <li key={item.text} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-background border border-border flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name={item.icon as any} size={16} className="text-accent" />
                      </div>
                      <span className="text-sm text-muted leading-relaxed">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Direct email */}
              <div className="bg-primary rounded-2xl p-8">
                <p className="text-white/60 text-sm mb-3">Prefer email?</p>
                <a
                  href="mailto:hello@businesssite.com"
                  className="text-white text-lg font-display font-light hover:text-accent transition-colors"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  hello@businesssite.com
                </a>
              </div>

            </div>
          </div>
        </section>

        {/* ── OFFICES ── */}
        <section
          className="py-20 px-6 md:px-12 border-t border-border"
          style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}
        >
          <div className="reveal mb-12">
            <span className="section-label mb-4 block">Our Offices</span>
            <h2
              className="text-3xl font-display font-light text-primary"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Find us
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {offices.map((office, i) => (
              <div
                key={office.city}
                className={`reveal reveal-delay-${i + 1}`}
                itemScope
                itemType="https://schema.org/LocalBusiness"
              >
                <meta itemProp="name" content={`BusinessSite ${office.city}`} />
                <div className="bg-card border border-border rounded-2xl p-8 h-full">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center">
                      <Icon name="MapPinIcon" size={16} className="text-accent" />
                    </div>
                    <h3
                      className="text-xl font-display font-light text-primary"
                      style={{ fontFamily: 'var(--font-display)' }}
                    >
                      {office.city}
                    </h3>
                  </div>

                  <address
                    className="not-italic space-y-3 text-muted text-sm leading-relaxed"
                    itemProp="address"
                    itemScope
                    itemType="https://schema.org/PostalAddress"
                  >
                    <div>
                      <span itemProp="streetAddress">{office.address}</span><br />
                      <span itemProp="addressLocality">{office.cityState.split(',')[0]}</span>
                      {', '}
                      <span itemProp="addressRegion">{office.cityState.split(', ')[1]?.split(' ')[0]}</span>
                      {' '}
                      <span itemProp="postalCode">{office.cityState.split(' ').pop()}</span><br />
                      <span itemProp="addressCountry">{office.country}</span>
                    </div>
                    <div className="pt-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <Icon name="PhoneIcon" size={14} className="text-muted flex-shrink-0" />
                        <a
                          href={`tel:${office.phone.replace(/[^+\d]/g, '')}`}
                          className="hover:text-accent transition-colors"
                          itemProp="telephone"
                        >
                          {office.phone}
                        </a>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="EnvelopeIcon" size={14} className="text-muted flex-shrink-0" />
                        <a
                          href={`mailto:${office.email}`}
                          className="hover:text-accent transition-colors"
                          itemProp="email"
                        >
                          {office.email}
                        </a>
                      </div>
                    </div>
                  </address>
                </div>
              </div>
            ))}
          </div>
        </section>

      </main>

      <Footer />
    </>
  );
}